import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { createWidget } from '@typeform/embed'
import '@typeform/embed/build/css/widget.css'
import { StructuredSources } from 'components/molecules'
import { AdBlock } from 'components/molecules/adBlock'
import { OutstreamVideoAd } from 'components/molecules/adBlock/outstreamVideoAd'
import { AuthorAndDate } from 'components/molecules/authorAndDate'
import { Sources } from 'components/molecules/sources'
import {
  SponsoredBy,
  StickySponsoredBy,
} from 'components/molecules/sponsoredBy'
import { AskDoctor } from 'components/organisms/askDoctor'
// import { CommentPopup } from 'components/organisms/commentPopup'
import { OpeningOfArticle } from 'components/organisms/openingOfArticle'
import { RelatedCategoriesList } from 'components/organisms/relatedCategoriesList'
import { RelatedPosts } from 'components/organisms/relatedPosts'
import { ArticlePageContext } from 'components/pages/article/index'
import innertext from 'innertext'
import {
  ArticleType as ArticleConstType,
  FEATURE_FLAG,
} from 'interfaces/constants'
import { AnchorRef, Article as ArticleType } from 'interfaces/types'
import { kebabCase, throttle } from 'lodash'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import ReactPlayer from 'react-player/youtube'
// import { useImage } from 'hooks/useImage'
import sanitizeHtml from 'sanitize-html'
import { AppContext } from 'state/appContext'
import {
  getAdTag,
  getCategorySlugFromArticleSlug,
  getCountryAdTag,
  getLocaleCategoryAdTag,
} from 'utils/ads'
import { normalizeWS } from 'utils/htmlHelper'
import { lazyLoadInstance } from 'utils/lazyLoad'
import { TRACK_GA } from 'utils/tracking'

import {
  getArticleCurrentCategory,
  isPreviewSlug,
} from '../../pages/article/utils'
import {
  ArticleContents,
  FormattedPostItem,
  FormattedPostItemContent,
} from './articleContents'
import {
  StyledArticle,
  StyledArticleBottomContainer,
  StyledArticleContainer,
  StyledArticleContentWrapper,
  StyledArticleCover,
  StyledArticleRightColumn,
  StyledContentWrapper,
  StyledDivider,
  StyledDividerUnion,
  StyledVideoWrapper,
} from './index.styled'
import KeyTakeAways from './KeyTakeAways'
import { getMetaTag } from './utils'

export type Props = {
  article: ArticleType
  isMobile: boolean
  handleCloseCommentPopup?: () => void
  commentCount?: number
  setCommentCount?: () => void
  index?: number
  isCommentPopupOpen?: boolean
  openAskDoctor?: (e: boolean) => void
}

export const getSectionId = (articleId: string, sectionText: string) => {
  return articleId + '-' + kebabCase(sectionText)
}

export const sanitize = (html: string) =>
  html &&
  sanitizeHtml(html || '', {
    allowedTags: false,
    allowedAttributes: false,
  })

const getTypeForm = (typeFormWidget: HTMLElement) => {
  if (!!typeFormWidget) {
    const url =
      typeFormWidget.getAttribute('data-url') ||
      typeFormWidget.getAttribute('href') ||
      ''
    const formId = url.match(/\/to\/([^/]+)\/?/)?.[1] || ''

    if (!!formId) {
      createWidget(formId, {
        container: typeFormWidget as HTMLElement,
        height: 500,
        disableAutoFocus: true,
      })
    }
  }
}
const processHTML = ({
  htmlContent,
}: // locale,
{
  htmlContent: string
}): FormattedPostItemContent[] => {
  const nextHTML = htmlContent
    .replace(/\[covid_19\]/gi, '')
    .replace(/rel="([^"]+)"/gi, (match) => {
      // Remove rel noreferrer
      return match.replace('noreferrer', '')
    })
    .replace(/<a.*?<\/a>/gi, (match) => {
      // Remove only these property from img tag
      const matchUrl = /href="(.*?)"/gi.exec(match)
      const url = matchUrl?.[1] || ''
      // force all url to https and add more slash at the end if not
      const httpsUrl =
        url.replace('http://', 'https://') + (!url.endsWith('/') ? '/' : '')
      const content = innertext(match)
        .replace(/&/g, '&amp;')
        .replace(/'/g, '&apos;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return match
        .replace(/href="(.*?)"/gi, `href="${httpsUrl}"`)
        .replace(
          '<a',
          `<a data-event-category="${TRACK_GA.ARTICLE_INTERNAL_LINK.category}" data-event-action="${content}" data-event-label="${url}"`,
        )
    })
    .replace(/<img([^>]+)>/gi, (match) =>
      match.replace(/src="(.*?)"/gi, (_, p1: string) => {
        return `src="${p1.replace('http://', 'https://')}"`
      }),
    )
    .replace(/<iframe([^>]+)>/gi, (match) => {
      return match.replace(/src="(.*?)"/gi, (_, p1: string) => {
        return `loading="lazy" src="${p1.replace('http://', 'https://')}"`
      })
    })

  const formattedContent =
    nextHTML
      ?.split('\n')
      .map((content) => normalizeWS(content))
      .filter((t) => {
        return (
          t.trim().length > 0 &&
          t
            .replace(/>(\s|&nbsp;)+</g, '><')
            .replace(/<p([^>]?)>/gi, '')
            .replace(/<\/p>/gi, '')
        )
      }) || []

  const res: FormattedPostItemContent[] = [
    {
      html: '',
    },
  ]
  formattedContent.forEach((formatted, index) => {
    if (formatted.includes('wp-embedded-content')) {
      let formattedWithIframe = formatted
      if (formatted.includes('blockquote')) {
        if (!formatted.includes('iframe')) return
        formattedWithIframe = formatted.split('</blockquote>')[1]
      }
      const matchHref = /href="(.*?)"/gi.exec(formattedWithIframe)
      let embedHref = matchHref?.[1] || ''
      if (embedHref.includes('/embed')) {
        embedHref = embedHref.slice(0, embedHref.indexOf('/embed'))
      }
      if (!embedHref.startsWith('/')) {
        embedHref = embedHref.replace('https://', '').replace('http://', '')
        embedHref = embedHref.slice(embedHref.indexOf('/'))
      }
      let embedSlug: string | undefined = embedHref
      if (!embedHref) {
        const matchSrc = /src="(.*?)"/gi.exec(formattedWithIframe)

        let embedSrc = matchSrc?.[1] || ''
        if (embedSrc.includes('/embed')) {
          embedSrc = embedSrc.slice(0, embedSrc.indexOf('/embed'))
        }
        if (!embedHref.startsWith('/')) {
          embedHref = embedHref.replace('https://', '').replace('http://', '')
          embedHref = embedHref.slice(embedHref.indexOf('/'))
        }
        embedSlug = embedSrc
      }
      if (embedSlug) {
        if (res[res.length - 1]) {
          res[res.length - 1].injectedSlug = embedSlug
        }
        if (index + 1 < formattedContent.length) {
          res.push({
            html: '',
          })
        }
      }
    } else {
      const formattedNew = formatted.replace(
        'id="mc4wp-form-1"',
        'id="mc4wp-form-1" style="display: none"',
      ) // remove form without breaking html

      res[res.length - 1].html += ' ' + formattedNew
    }
  })
  return res
}

export const Article = ({
  article,
  isMobile,
  index,
  isCommentPopupOpen,
  handleCloseCommentPopup,
  commentCount,
  setCommentCount,
  openAskDoctor,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const router = useRouter()
  const { locale = 'vi-VN' } = router
  const sL = locale || 'vi-VN' // standardized locale

  const { state } = useContext(AppContext)
  const { userInfo } = state || {}

  const articlePageContextValue = useContext(ArticlePageContext)
  const topContentRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const onScroll = throttle(() => {
      if (topContentRef.current) {
        const { top, bottom } = topContentRef.current.getBoundingClientRect()
        const halfScreen = window.innerHeight / 2
        const isInView = top <= halfScreen && bottom >= halfScreen
        setInView(isInView)
        // if (isInView && articlePageContextValue?.setCurrentProgress) {
        //   articlePageContextValue.setCurrentProgress(
        //     Math.floor(((halfScreen - top) / (bottom - top)) * 100) / 100,
        //   )
        // }
      }
    }, 1000)

    document.addEventListener('scroll', onScroll)
    return () => document.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      const typeFormWidgets = document.querySelectorAll(
        `#a-${article.id || ''} [data-url*="typeform.com/"], #a-${
          article.id || ''
        } [href*="typeform.com/"]`,
      )
      typeFormWidgets.forEach((f) => getTypeForm(f as HTMLElement))
    }, 2000)
  }, [])
  useEffect(() => {
    if (inView) {
      // scroll down to top of an article
      const currentSlug = window.location.pathname
        .split('/')
        .filter(Boolean)
        .pop()
      const articleSlug = article.permalink.split('/').filter(Boolean).pop()
      if (articleSlug !== currentSlug) {
        articlePageContextValue.setCurrentArticle?.(article)
        if (!isPreviewSlug())
          router.push(
            {
              href: article.permalink,
              query: {
                articleId: String(article.id),
              },
            },
            article.permalink,
            {
              shallow: true,
              scroll: false,
            },
          )
      }
    }
  }, [inView])

  const tableOfContentsData: AnchorRef[] = useMemo(() => {
    if (
      article.sections &&
      article.sections.filter((s) => s.title).length > 0
    ) {
      return article.sections
        .filter((s) => s.title)
        .map((section) => ({
          label: section.title,
          id: getSectionId(String(article.id), section.title),
        }))
    }
    return []
  }, [article.sections])

  const formattedPostContents: FormattedPostItem[] = useMemo(() => {
    // Because sections call processHTML multiple time, so adcount must be put outside to keep track
    if (article.sections && article.sections.length > 0) {
      const sections = article.sections.map((section) => {
        return {
          title: section.title || '',
          contents: processHTML({
            htmlContent: section.content || '',
          }).map((content) => ({
            ...content,
            html: sanitize(content.html),
          })),
        }
      })
      if (article.postContent)
        return [
          {
            contents: processHTML({
              htmlContent: article.postContent || '',
            }).map((content) => ({
              ...content,
              html: sanitize(content.html),
            })),
          },
          ...sections,
        ]
      return sections
    }
    return [
      {
        contents: processHTML({
          htmlContent: article.postContent || '',
        }).map((content) => ({
          ...content,
          html: sanitize(content.html),
        })),
      },
    ]
  }, [
    article.sections?.length || 0,
    article.postContent?.length || 0,
    FEATURE_FLAG.articleType,
    process.browser,
  ])

  const currentCategory = getArticleCurrentCategory(article)

  // const { src, blur } = useImage(
  //   article.featureImageMobileXs,
  //   isMobile ? article.featureImageMobile : article.featureImage,
  //   undefined,
  // )

  const contentAdTag = getLocaleCategoryAdTag(
    sL,
    getCategorySlugFromArticleSlug(article.permalink),
  )

  const adUnitPathDesktop = getAdTag({
    countryAdTag: getCountryAdTag(sL, {
      isMobile: false,
    }),
    contentAdTag,
  })

  const adUnitPathMobile = getAdTag({
    countryAdTag: getCountryAdTag(sL, {
      isMobile: true,
    }),
    contentAdTag,
  })
  // const adUnitPath = getAdTag({
  //   countryAdTag: getCountryAdTag(sL, {
  //     isMobile,
  //   }),
  //   contentAdTag,
  // })

  useEffect(() => {
    if (lazyLoadInstance) {
      lazyLoadInstance.update()
    }
  }, [formattedPostContents])

  const articleContentId = useMemo(() => {
    const regex = /name="content_id".*content="(.*?)"/gi
    const match = regex.exec(article.metaTag || '')
    return match?.[1] || ''
  }, [article.metaTag])

  const customTargetings = {
    Is_Sponsored: [article?.isSponsored ? 1 : 0],
    Is_Expert_Article: [
      article?.expert?.id ||
      (article?.articleReviewed?.expert?.ID &&
        article?.articleReviewed?.is_author)
        ? 1
        : 0,
    ],
    // Is_Affiliate: [article?.hasAffiliate ? 1 : 0],
    Keywords: [
      innertext(getMetaTag(article?.metaTag || '', 'content_tags') || ''),
    ],
  }

  return (
    <>
      <StyledArticleContainer>
        <AskDoctor
          postCount={commentCount}
          setPostCount={setCommentCount}
          handleClose={handleCloseCommentPopup}
          isCommentPopupOpen={isCommentPopupOpen}
        />
        {
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .article-content-wrapper {
                font-family: ${
                  sL === 'km-KH' ? 'Battambang' : 'Open Sans'
                }, -apple-system, BlinkMacSystemFont, 'Segoe UI',
                    Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif !important;
              }
              /* font-size */
              .article-content-wrapper p:not(#mc_embed_signup),
              .article-content-wrapper ul,
              .article-content-wrapper li,
              .article-content-wrapper form,
              .article-content-wrapper p {
                font-size: 1em !important;
              }
              .article-content-wrapper h1 *,
              .article-content-wrapper h2 *,
              .article-content-wrapper h3 *,
              .article-content-wrapper h4 *,
              .article-content-wrapper h5 *,
              .article-content-wrapper h6 * {
                font-size: inherit !important;
                font-weight: inherit !important;
              }
              .article-content-wrapper h2 {
                font-size: 1.5em !important;
              }
              .article-content-wrapper h3 {
                font-size: 1.4em !important;
              }
              .article-content-wrapper h4 {
                font-size: 1.3em !important;
              }
              .article-content-wrapper h5 {
                font-size: 1.2em !important;
              }
              .article-content-wrapper div.article-image-alt,
              .article-content-wrapper figcaption {
                font-size: 0.8em !important;
              }

              .article-content-wrapper a :not(a, table) {
                color: #2d87f3 !important;
              }
              .article-content-wrapper :not(a, table) {
                color: #262626;
              }

              .article-content-wrapper a {
                color: #2d87f3 !important;
              }

              .article-content-wrapper a * {
                color: #2d87f3 !important;
              }

              .article-content-wrapper button.lead-modal__btn {
                color: #ffffff !important;
              }
      
              .article-content-wrapper * {
              font-family: Inter, 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI',
                Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif !important;
              }
      
              .article-content-wrapper figure {
                width: 100% !important;
              }

              .article-content-wrapper table {
                border-collapse: collapse;
                width: 100%;
                max-width: 100%;
                overflow-x: auto;
                height: auto !important;
              }

              img {
                border-radius: 6px;
              }
            `,
            }}
          />
        }

        <StyledArticle
          id={`a-${article.id}`}
          ref={topContentRef}
          data-url={article.permalink} // for lead tool
        >
          <AuthorAndDate
            expert={article.expert}
            author={article.author}
            articleReviewed={article.reviewed}
            reviewer={article.reviewer || undefined}
            typeUpdate={
              new Date(article.postDate) === new Date(article.postModified)
                ? 'published'
                : 'lastUpdated'
            }
            updatedAt={article.modifiedTime || ''}
          />
          <StyledContentWrapper>
            {article?.isSponsored ? (
              <StickySponsoredBy sponsoredType="article" />
            ) : null}
            <OpeningOfArticle
              expert={article.expert}
              header={article.postTitle}
              author={article.author}
              reviewer={article.reviewer || undefined}
              articleReviewed={article.reviewed}
              updatedAt={article.modifiedTime || ''}
              tableOfContents={tableOfContentsData}
              className="article-opening"
              jumpingLink={article?.jumpingLink}
              isPremium={article?.isPremium}
            />
            {article.sponsor && article.sponsor.logo ? (
              <SponsoredBy
                isMobile={isMobile}
                image={article.sponsor.logo}
                href={article.sponsor.link}
              />
            ) : null}
            {!article.videoInfo?.video_id &&
              !!(article.featureImage || article.featureImageMobile) && (
                <StyledArticleCover>
                  <div className="ad-image-wrapper">
                    {
                      <img
                        loading="lazy"
                        style={{
                          objectFit: 'cover',
                        }}
                        src={
                          isMobile
                            ? article.featureImageMobile
                            : article.featureImage
                        }
                        alt={article.postTitle}
                      />
                    }
                    {/* {isMobile ? (
                      <AdBlock
                        className="ad-image"
                        divId={`div-gpt-ad-inimage-mobile-${article.id}`}
                        adUnitPath={adUnitPath}
                        size={[390, 70]}
                        pos={['InImage']}
                        contentId={articleContentId}
                        customTargetings={customTargetings}
                      />
                    ) : (
                      <AdBlock
                        className="ad-image"
                        divId={`div-gpt-ad-inimage-desktop-${article.id}`}
                        adUnitPath={adUnitPath}
                        size={[702, 100]}
                        pos={['InImage']}
                        contentId={articleContentId}
                        customTargetings={customTargetings}
                      />
                    )} */}
                  </div>
                </StyledArticleCover>
              )}
            {/* <AdBlock
              className="article-ad"
              divId={`div-gpt-ad-afterimage-${article.id}`}
              adUnitPath={adUnitPath}
              size={[1, 1]}
              pos={['AfterImage']}
              contentId={articleContentId}
            /> */}
            <OutstreamVideoAd
              categorySlug={getCategorySlugFromArticleSlug(article.permalink)}
            />
            {article.videoInfo?.video_id ? (
              <>
                <StyledVideoWrapper>
                  <ReactPlayer
                    url={`https://www.youtube.com/watch?v=${article.videoInfo.video_id}`}
                    controls
                    width="100%"
                  />
                </StyledVideoWrapper>
                {!!article.keyTakeaways && (
                  <KeyTakeAways keyTakeaways={article.keyTakeaways} />
                )}
              </>
            ) : (
              <>
                {!!article.keyTakeaways && (
                  <KeyTakeAways keyTakeaways={article.keyTakeaways} />
                )}
                <ArticleContents
                  formattedPostContents={formattedPostContents}
                  adUnitPath={isMobile ? adUnitPathMobile : adUnitPathDesktop}
                  articleId={String(article.id)}
                  spotlight={article?.spotlight || undefined}
                  postIntro={article.excerpt}
                  openAskDoctor={openAskDoctor}
                  articleLink={article.permalink}
                  articleIsPremium={article.isPremium}
                  isLoggedIn={!!userInfo?.id}
                  customTargetings={customTargetings}
                />
              </>
            )}

            <StyledArticleContentWrapper
              data-size={articlePageContextValue.articleFontSize || 'small'}
              className="disclaimer article-content-wrapper"
              // data-amp-bind-size="largerFontAmp"
            >
              <p className="end-of-article">
                {f({ id: 'articlePage.disclaimer' })}
              </p>
            </StyledArticleContentWrapper>
            {article.sources && (
              <Sources
                sources={sanitize(article.sources)}
                size="sm"
                isMobile={isMobile}
              />
            )}
            {/* <div
              className={`article-ad-container ${
                article.relatedArticles && article.relatedArticles.length < 3
                  ? 'ad-avoid-bottom'
                  : ''
              }`}
            >
              <AdBlock
                className="article-ad"
                divId={`div-gpt-ad-endbanner-${article.id}`}
                adUnitPath={adUnitPath}
                size={[300, 250]}
                pos={['EndBanner']}
                contentId={articleContentId}
                isHidden={!isMobile}
              />
            </div> */}
          </StyledContentWrapper>
          <StyledArticleBottomContainer>
            {(FEATURE_FLAG.articleType === ArticleConstType.ArticleType1 ||
              FEATURE_FLAG.articleType === ArticleConstType.ArticleType2) &&
              article.relatedCategories &&
              article.relatedCategories.length > 0 && (
                <>
                  <StyledDivider type="solid" />
                  <StyledContentWrapper>
                    <RelatedCategoriesList
                      isMobile={isMobile}
                      categories={article.relatedCategories}
                      currentCategoryPermalink={currentCategory?.permalink}
                    />
                  </StyledContentWrapper>
                </>
              )}
            {(FEATURE_FLAG.articleType === ArticleConstType.ArticleType2 ||
              FEATURE_FLAG.articleType === ArticleConstType.ArticleType3) &&
            article.relatedArticles?.length ? (
              <RelatedPosts
                isMobile={isMobile}
                articles={article.relatedArticles?.filter(
                  (article) => article.id,
                )}
              />
            ) : null}
            <StyledDividerUnion />
          </StyledArticleBottomContainer>
        </StyledArticle>
        <StyledArticleRightColumn>
          {!isMobile && tableOfContentsData?.length >= 1 && (
            <StructuredSources
              size="md"
              sources={tableOfContentsData}
              className="article-table-content"
            />
          )}
          {index === 0 && (
            <div
              className="hhg-lead-sidebar"
              style={{
                maxWidth: '100%',
              }}
            />
          )}
          {!isMobile && (
            <div className="article-desktop-ad-container">
              <div className="article-desktop-ad-wrapper">
                <AdBlock
                  className="article-desktop-ad"
                  adUnitPath={adUnitPathDesktop}
                  size={[
                    [300, 250],
                    [300, 600],
                  ]}
                  divId={'div-gpt-ad-topbanner-desktop-' + articleContentId}
                  pos={['TopBanner']}
                  contentId={articleContentId}
                  isHidden={isMobile}
                  customTargetings={customTargetings}
                  onlyRefreshInview
                  refreshInterval={30}
                />
              </div>
              <div className="article-desktop-ad-wrapper">
                <AdBlock
                  className="article-desktop-ad"
                  adUnitPath={adUnitPathDesktop}
                  size={[
                    [300, 250],
                    [160, 600],
                  ]}
                  divId={'div-gpt-ad-midbanner1-desktop-' + articleContentId}
                  pos={['MidBanner1']}
                  contentId={articleContentId}
                  isHidden={isMobile}
                  customTargetings={customTargetings}
                  onlyRefreshInview
                  refreshInterval={30}
                />
              </div>
              <div className="article-desktop-ad-wrapper">
                <AdBlock
                  className="article-desktop-ad"
                  adUnitPath={adUnitPathDesktop}
                  size={[
                    [300, 250],
                    [160, 600],
                  ]}
                  divId={'div-gpt-ad-midbanner2-desktop-' + articleContentId}
                  pos={['MidBanner2']}
                  contentId={articleContentId}
                  isHidden={isMobile}
                  customTargetings={customTargetings}
                  onlyRefreshInview
                  refreshInterval={30}
                />
              </div>
              {/* <div className="article-desktop-ad-wrapper">
                <AdBlock
                  className="article-desktop-ad"
                  adUnitPath={adUnitPathDesktop}
                  size={[300, 250]}
                  divId={'div-gpt-ad-endbanner-desktop-' + articleContentId}
                  pos={['EndBanner']}
                  contentId={articleContentId}
                  isHidden={isMobile}
                  customTargetings={customTargetings}
                />
              </div> */}
            </div>
          )}
        </StyledArticleRightColumn>
      </StyledArticleContainer>
    </>
  )
}
